// frontend/src/pages/AdminPanelPage.tsx - UPDATED FOR HOOKS/STORE INTEGRATION
// MODIFIED FUNCTION: Updated to use new API hooks, store, and utils following the pattern of CorrespondencesPage, ResponsesPage, and ProjectsPage

import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { cn, formatDate } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';

// UPDATED: Use hooks following new architecture
import {
  useDashboard,
  useDashboardMetrics,
  useDashboardHealth,
  useRefreshDashboard
} from '@/hooks/useDashboard';

import {
  useOrganizations
} from '@/hooks/useOrganizations';

import {
  useUsers
} from '@/hooks/useUsers';

// UPDATED: Use API client for system actions
import apiClient from '@/api';

// UPDATED: Use API types
import type {
  UserRole
} from '@/api/types';

// ===== INTERFACES =====

interface SystemStats {
  summary: {
    totalOrgs: number;
    activeOrgs: number;
    totalUsers: number;
    totalProjects: number;
    totalStorage: number;
  };
  organizations: Array<{
    _id: string;
    name: string;
    userCount: number;
    isActive: boolean;
    createdAt: string;
  }>;
  users: {
    byRole: Record<UserRole, number>;
    recentActivity: number;
  };
  system: {
    uptime: string;
    health: 'good' | 'warning' | 'error';
    lastBackup: string;
  };
}

interface SystemAction {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  requiresConfirmation: boolean;
  buttonVariant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  icon: string;
}

interface ActionResult {
  success: boolean;
  message: string;
  timestamp: Date;
  details?: any;
}

// ===== UTILITY COMPONENTS =====

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: string;
  color: string;
  description?: string;
  trend?: {
    direction: 'up' | 'down' | 'stable';
    value: number;
    period: string;
  };
}> = memo(({ title, value, icon, color, description, trend }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center">
        <div className={cn(
          'w-12 h-12 rounded-lg flex items-center justify-center',
          color
        )}>
          <span className="text-2xl">{icon}</span>
        </div>
        <div className="ml-4 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {title}
            </h3>
            {trend && (
              <div className={cn(
                'flex items-center text-xs',
                trend.direction === 'up' ? 'text-green-600' : 
                trend.direction === 'down' ? 'text-red-600' : 'text-gray-500'
              )}>
                <span className="mr-1">
                  {trend.direction === 'up' ? '↗' : trend.direction === 'down' ? '↘' : '→'}
                </span>
                {trend.value}% {trend.period}
              </div>
            )}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {value}
          </div>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

const SystemActionButton: React.FC<{
  action: SystemAction;
  onExecute: (action: SystemAction) => void;
  isExecuting: boolean;
  disabled?: boolean;
}> = memo(({ action, onExecute, isExecuting, disabled = false }) => {
  const { confirm } = useConfirmation();

  const handleClick = useCallback(async () => {
    if (action.requiresConfirmation) {
      const confirmed = await confirm({
        title: action.name,
        message: `Are you sure you want to ${action.name.toLowerCase()}?\n\n${action.description}`,
        confirmText: 'Proceed',
        variant: action.buttonVariant === 'danger' ? 'danger' : 'warning',
      });
      if (confirmed) {
        onExecute(action);
      }
    } else {
      onExecute(action);
    }
  }, [action, onExecute, confirm]);

  return (
    <Button
      variant={action.buttonVariant}
      onClick={handleClick}
      disabled={isExecuting || disabled}
      className="w-full justify-start"
    >
      {isExecuting && <LoadingSpinner size="sm" />}
      <span className="mr-2">{action.icon}</span>
      <div className="flex-1 text-left">
        <div className="font-medium">{action.name}</div>
        <div className="text-xs opacity-75">{action.description}</div>
      </div>
    </Button>
  );
});

const ActivityItem: React.FC<{
  activity: {
    id: string;
    action: string;
    details: string;
    timestamp: string;
    type: 'info' | 'success' | 'warning' | 'error';
  };
}> = memo(({ activity }) => {
  const getTypeColor = (type: 'info' | 'success' | 'warning' | 'error') => {
    switch (type) {
      case 'success': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'warning': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'error': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      default: return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    }
  };

  return (
    <div className="flex items-start space-x-3 p-4">
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
        getTypeColor(activity.type)
      )}>
        <span className="text-sm font-medium">
          {activity.type === 'success' ? '✓' : 
           activity.type === 'error' ? '!' :
           activity.type === 'warning' ? '⚠' : 'ℹ'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
            {activity.action}
          </h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(activity.timestamp)}
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          {activity.details}
        </p>
      </div>
    </div>
  );
});

// ===== SYSTEM ACTIONS CONFIGURATION =====

const SYSTEM_ACTIONS: SystemAction[] = [
  {
    id: 'backup',
    name: 'Create System Backup',
    description: 'Generate a full system backup',
    endpoint: '/admin/system/backup',
    method: 'POST',
    requiresConfirmation: true,
    buttonVariant: 'primary',
    icon: '💾'
  },
  {
    id: 'clear-cache',
    name: 'Clear System Cache',
    description: 'Clear all application caches',
    endpoint: '/admin/system/cache',
    method: 'DELETE',
    requiresConfirmation: false,
    buttonVariant: 'secondary',
    icon: '🗑️'
  },
  {
    id: 'sync-emails',
    name: 'Sync All Emails',
    description: 'Force synchronization of all email accounts',
    endpoint: '/admin/emails/sync-all',
    method: 'POST',
    requiresConfirmation: false,
    buttonVariant: 'success',
    icon: '🔄'
  },
  {
    id: 'maintenance-mode',
    name: 'Toggle Maintenance',
    description: 'Enable/disable maintenance mode',
    endpoint: '/admin/system/maintenance',
    method: 'POST',
    requiresConfirmation: true,
    buttonVariant: 'warning',
    icon: '🔧'
  },
  {
    id: 'restart-services',
    name: 'Restart Services',
    description: 'Restart background services',
    endpoint: '/admin/system/restart',
    method: 'POST',
    requiresConfirmation: true,
    buttonVariant: 'danger',
    icon: '⚡'
  }
];

// ===== ERROR BOUNDARY =====

class AdminPanelErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('AdminPanelPage Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Admin Panel Error</h2>
          <p className="text-gray-600 mb-4">Something went wrong loading the admin panel.</p>
          <Button onClick={() => this.setState({ hasError: false })}>
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ===== MAIN COMPONENT =====

export const AdminPanelPage: React.FC = () => {
  // ===== HOOKS =====
  
  const { user: currentUser, isAuthenticated } = useAuthStore();
  const { setGlobalError, clearGlobalError, addNotification } = useAppStore();

  // ===== LOCAL STATE =====

  const [actionResults, setActionResults] = useState<Record<string, ActionResult>>({});
  const [executingActions, setExecutingActions] = useState<Set<string>>(new Set());
  const [selectedTimeframe, setSelectedTimeframe] = useState<'day' | 'week' | 'month'>('week');

  // ===== COMPUTED VALUES =====

  // Check permissions - implementors and admins can access admin panel
  const isImplementor = currentUser?.role === 'implementor';
  const isOrgAdmin = currentUser?.role === 'admin';
  const hasAdminAccess = useMemo(() => {
    return isImplementor || isOrgAdmin;
  }, [isImplementor, isOrgAdmin]);

  // ===== ORG-SCOPED STATE (for admin role) =====
  const [orgMetrics, setOrgMetrics] = useState<any>(null);
  const [orgMetricsLoading, setOrgMetricsLoading] = useState(false);
  const [orgMetricsError, setOrgMetricsError] = useState<string | null>(null);

  const fetchOrgMetrics = useCallback(async () => {
    setOrgMetricsLoading(true);
    setOrgMetricsError(null);
    try {
      const result = await apiClient.admin.getOrgMetrics();
      setOrgMetrics((result as any)?.data || result);
    } catch (err: any) {
      setOrgMetricsError(err?.response?.data?.error || err.message || 'Failed to load org metrics');
    } finally {
      setOrgMetricsLoading(false);
    }
  }, []);

  // ===== API HOOKS =====

  // System-wide metrics for implementors
  const {
    loading: systemLoading,
    error: systemError,
    refetch: refetchSystemMetrics
  } = useDashboardMetrics({ period: 'week' }, {
    immediate: isImplementor
  });

  // Dashboard summary
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError
  } = useDashboard({
    immediate: isImplementor
  });

  // System health
  const {
    loading: healthLoading,
    error: healthError,
    refetch: refetchHealth
  } = useDashboardHealth({
    immediate: isImplementor
  });

  // Organizations for admin overview
  const {
    data: organizationsData,
    loading: orgsLoading,
    error: orgsError
  } = useOrganizations({
    limit: 10,
    status: 'all'
  }, {
    immediate: isImplementor
  });

  // Users overview
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError
  } = useUsers({
    limit: 10
  }, {
    immediate: hasAdminAccess
  });

  // Refresh dashboard mutation
  const {
    mutate: refreshDashboard,
    loading: refreshLoading
  } = useRefreshDashboard({
    onSuccess: () => {
      addNotification?.({
        type: 'success',
        title: 'Dashboard Refreshed',
        message: 'All dashboard data has been updated successfully.'
      });
    },
    onError: (error) => {
      setGlobalError(error.message || 'Failed to refresh dashboard');
    }
  });

  // ===== EFFECTS =====

  // Clear errors when component mounts
  useEffect(() => {
    return () => clearGlobalError();
  }, [clearGlobalError]);

  // Fetch org metrics for admin users
  useEffect(() => {
    if (isOrgAdmin && hasAdminAccess) {
      fetchOrgMetrics();
    }
  }, [isOrgAdmin, hasAdminAccess, fetchOrgMetrics]);

  // ===== COMPUTED VALUES =====

  const isLoading = isImplementor
    ? (systemLoading || dashboardLoading || healthLoading || orgsLoading || usersLoading)
    : (orgMetricsLoading || usersLoading);
  const hasErrors = isImplementor
    ? (systemError || dashboardError || healthError || orgsError || usersError)
    : (orgMetricsError || usersError);

  // Aggregate system statistics
  const systemStats = useMemo((): SystemStats => {
    const orgs = organizationsData?.data || [];
    const users = usersData?.data || [];
    
    // Use dashboard metrics if available, fallback to basic data
    const dashboardCounts = dashboardData?.counts || {};
    
    return {
      summary: {
        totalOrgs: orgs.length,
        activeOrgs: orgs.filter(o => o.isActive).length,
        totalUsers: users.length,
        totalProjects: (dashboardCounts as any)?.projects || 0, // Type assertion to handle unknown structure
        totalStorage: 0 // Storage metrics would need specific endpoint
      },
      organizations: orgs.slice(0, 5).map(org => ({
        _id: org._id || org.id || '',
        name: org.name,
        userCount: org.userCount || 0,
        isActive: org.isActive ?? true,
        createdAt: org.createdAt
      })),
      users: {
        byRole: {
          implementor: users.filter(u => u.role === 'implementor').length,
          admin: users.filter(u => u.role === 'admin').length,
          contributor: users.filter(u => u.role === 'contributor').length,
          user: users.filter(u => u.role === 'user').length
        },
        recentActivity: users.filter(u => u.isActive).length
      },
      system: {
        uptime: '99.9%', // Would need specific health endpoint
        health: hasErrors ? 'error' : 'good',
        lastBackup: 'System backup not configured'
      }
    };
  }, [organizationsData, usersData, dashboardData, hasErrors]);

  // Recent activities
  const recentActivities = useMemo(() => {
    const activities = [];
    
    if (organizationsData?.data?.length) {
      activities.push({
        id: 'org-created',
        action: 'Organization Activity',
        details: `${systemStats.summary.totalOrgs} organizations, ${systemStats.summary.activeOrgs} active`,
        timestamp: new Date().toISOString(),
        type: 'info' as const
      });
    }

    if (systemStats.users.recentActivity > 0) {
      activities.push({
        id: 'user-activity',
        action: 'User Activity',
        details: `${systemStats.users.recentActivity} active users today`,
        timestamp: new Date().toISOString(),
        type: 'success' as const
      });
    }

    if (systemStats.system.health !== 'good') {
      activities.push({
        id: 'system-health',
        action: 'System Health Check',
        details: `System status: ${systemStats.system.health}`,
        timestamp: new Date().toISOString(),
        type: (systemStats.system.health === 'warning' ? 'warning' : 'error') as 'warning' | 'error'
      });
    }

    activities.push({
      id: 'system-stats',
      action: 'System Statistics Updated',
      details: `${systemStats.summary.totalProjects} projects, ${Math.round(systemStats.summary.totalStorage / (1024 * 1024))} MB storage`,
      timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      type: 'info' as const
    });

    return activities.slice(0, 6);
  }, [systemStats]);

  // ===== EVENT HANDLERS =====

  const handleExecuteAction = useCallback(async (action: SystemAction) => {
    setExecutingActions(prev => new Set([...prev, action.id]));

    try {

      let result;

      // Use the new admin API endpoints
      switch (action.id) {
        case 'backup':
          result = await apiClient.admin.createBackup();
          break;
        case 'clear-cache':
          result = await apiClient.admin.clearCache();
          break;
        case 'sync-emails':
          result = await apiClient.admin.syncAllEmails();
          break;
        case 'maintenance-mode':
          // Toggle maintenance mode (you might want to track current state)
          result = await apiClient.admin.toggleMaintenance(true);
          break;
        case 'restart-services':
          result = await apiClient.admin.restartServices();
          break;
        default:
          throw new Error(`Unknown action: ${action.id}`);
      }

      const actionResult: ActionResult = {
        success: true,
        message: (result as any)?.data?.message || (result as any)?.message || `${action.name} completed successfully`,
        timestamp: new Date(),
        details: (result as any)?.data
      };

      setActionResults(prev => ({
        ...prev,
        [action.id]: actionResult
      }));

      addNotification?.({
        type: 'success',
        title: action.name,
        message: actionResult.message
      });

      // Refresh relevant data after successful actions
      if (['backup', 'maintenance-mode', 'restart-services'].includes(action.id)) {
        refetchSystemMetrics();
        refetchHealth();
      }

    } catch (error: any) {
      console.error(`Admin action failed:`, error);

      const actionResult: ActionResult = {
        success: false,
        message: error?.response?.data?.message || error.message || `Failed to execute ${action.name}`,
        timestamp: new Date()
      };

      setActionResults(prev => ({
        ...prev,
        [action.id]: actionResult
      }));

      addNotification?.({
        type: 'error',
        title: action.name,
        message: actionResult.message,
        priority: 'high'
      });
    } finally {
      setExecutingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(action.id);
        return newSet;
      });
    }
  }, [addNotification, setGlobalError, refetchSystemMetrics, refetchHealth]);

  const handleRefreshAll = useCallback(() => {
    if (isImplementor) {
      refreshDashboard();
      refetchSystemMetrics();
      refetchHealth();
    } else if (isOrgAdmin) {
      fetchOrgMetrics();
    }
  }, [isImplementor, isOrgAdmin, refreshDashboard, refetchSystemMetrics, refetchHealth, fetchOrgMetrics]);

  // ===== RENDER GUARDS =====

  // Check authentication
  if (!isAuthenticated || !currentUser) {
    return (
      <PageLayout title="Admin Panel" subtitle="Access denied">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Authentication Required
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please log in to access the admin panel.
          </p>
        </div>
      </PageLayout>
    );
  }

  // Check admin permissions
  if (!hasAdminAccess) {
    return (
      <PageLayout title="Admin Panel" subtitle="Access denied">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Access Denied
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Only administrators can access the admin panel. Contact your system administrator if you need access.
          </p>
        </div>
      </PageLayout>
    );
  }

  // ===== ORG ADMIN VIEW =====
  if (isOrgAdmin) {
    const users = usersData?.data || [];
    const om = orgMetrics;

    return (
      <AdminPanelErrorBoundary>
        <PageLayout
          title="Admin Panel"
          subtitle={om ? `Organization: ${om.organization?.name}` : 'Organization Administration'}
          actions={
            <Button variant="outline" onClick={handleRefreshAll} disabled={isLoading || refreshLoading}>
              {(isLoading || refreshLoading) && <LoadingSpinner size="sm" />}
              Refresh
            </Button>
          }
          loading={isLoading}
        >
          <div className="space-y-6">
            {hasErrors && (
              <ErrorAlert
                message={typeof hasErrors === 'string' ? hasErrors : 'Failed to load some admin panel data.'}
                onClose={clearGlobalError}
              />
            )}

            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {!isLoading && om && (
              <>
                {/* Organization Info Banner */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🏢</span>
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">{om.organization?.name}</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        Plan: <span className="capitalize font-medium">{om.organization?.plan}</span>
                        {' · '}
                        Status: <span className={om.organization?.isActive ? 'text-green-600' : 'text-red-600'}>
                          {om.organization?.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {' · '}
                        AI: {om.organization?.isAIEnabled ? '✓ Enabled' : '✗ Disabled'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                  <MetricCard title="Users" value={om.counts?.users || 0} icon="👥" color="bg-blue-100 dark:bg-blue-900/20" />
                  <MetricCard title="Projects" value={om.counts?.projects || 0} icon="📂" color="bg-green-100 dark:bg-green-900/20" />
                  <MetricCard title="Emails" value={om.counts?.emails || 0} icon="📧" color="bg-yellow-100 dark:bg-yellow-900/20" />
                  <MetricCard title="Correspondences" value={om.counts?.correspondences || 0} icon="💬" color="bg-purple-100 dark:bg-purple-900/20" />
                  <MetricCard title="Attachments" value={om.counts?.attachments || 0} icon="📎" color="bg-orange-100 dark:bg-orange-900/20" />
                  <MetricCard title="Storage" value={`${om.storage?.totalMB || '0'} MB`} icon="💾" color="bg-indigo-100 dark:bg-indigo-900/20" />
                </div>

                {/* User Statistics */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">User Statistics</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">User distribution by role</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {(['admin', 'contributor', 'user'] as const).map((role) => (
                        <div key={role} className="text-center">
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {users.filter(u => u.role === role).length}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                            {role}s
                          </div>
                        </div>
                      ))}
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {users.length}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Total
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </PageLayout>
      </AdminPanelErrorBoundary>
    );
  }

  // ===== RENDER =====

  const pageActions = (
    <div className="flex items-center space-x-4">
      <div className="flex items-center space-x-2">
        <label htmlFor="timeframe" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Timeframe:
        </label>
        <select
          id="timeframe"
          value={selectedTimeframe}
          onChange={(e) => setSelectedTimeframe(e.target.value as typeof selectedTimeframe)}
          className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="day">24 Hours</option>
          <option value="week">7 Days</option>
          <option value="month">30 Days</option>
        </select>
      </div>

      <Button
        variant="outline"
        onClick={handleRefreshAll}
        disabled={isLoading || refreshLoading}
      >
        {refreshLoading && <LoadingSpinner size="sm" />}
        Refresh All
      </Button>
    </div>
  );

  return (
    <AdminPanelErrorBoundary>
      <PageLayout 
        title="Admin Panel" 
        subtitle="System administration and monitoring dashboard"
        actions={pageActions}
        loading={isLoading}
      >
        <div className="space-y-6">
          {/* Error Display */}
          {hasErrors && (
            <ErrorAlert
              message="There was an error loading admin panel data. Some information may be incomplete."
              onClose={clearGlobalError}
            />
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* System Overview Metrics */}
          {!isLoading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <MetricCard
                  title="Total Organizations"
                  value={systemStats.summary.totalOrgs}
                  icon="🏢"
                  color="bg-blue-100 dark:bg-blue-900/20"
                  description={`${systemStats.summary.activeOrgs} active`}
                />
                
                <MetricCard
                  title="Total Users"
                  value={systemStats.summary.totalUsers}
                  icon="👥"
                  color="bg-green-100 dark:bg-green-900/20"
                  description={`${systemStats.users.recentActivity} active today`}
                />
                
                <MetricCard
                  title="System Health"
                  value={systemStats.system.health === 'good' ? 'Healthy' : systemStats.system.health}
                  icon={systemStats.system.health === 'good' ? '✅' : systemStats.system.health === 'warning' ? '⚠️' : '❌'}
                  color={`${systemStats.system.health === 'good' ? 'bg-green-100 dark:bg-green-900/20' : 
                    systemStats.system.health === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20' : 'bg-red-100 dark:bg-red-900/20'}`}
                  description={`Uptime: ${systemStats.system.uptime}`}
                />
                
                <MetricCard
                  title="Storage Used"
                  value={`${Math.round(systemStats.summary.totalStorage / (1024 * 1024))} MB`}
                  icon="💾"
                  color="bg-purple-100 dark:bg-purple-900/20"
                  description="System storage usage"
                />
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* System Actions */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">System Actions</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Administrative operations</p>
                  </div>
                  <div className="p-6 space-y-3">
                    {SYSTEM_ACTIONS.map((action) => (
                      <SystemActionButton
                        key={action.id}
                        action={action}
                        onExecute={handleExecuteAction}
                        isExecuting={executingActions.has(action.id)}
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>

                {/* Recent Activities */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Recent Activities</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">System events and changes</p>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                    {recentActivities.length > 0 ? (
                      recentActivities.map((activity) => (
                        <ActivityItem key={activity.id} activity={activity} />
                      ))
                    ) : (
                      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                        No recent activities
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Results */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Action Results</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Recent command outcomes</p>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                    {Object.entries(actionResults).length > 0 ? (
                      Object.entries(actionResults)
                        .sort(([,a], [,b]) => b.timestamp.getTime() - a.timestamp.getTime())
                        .map(([actionId, result]) => (
                          <div key={actionId} className="p-4">
                            <div className={cn(
                              'flex items-center justify-between p-3 rounded-md',
                              result.success
                                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                            )}>
                              <div className="flex-1">
                                <div className={cn(
                                  'font-medium text-sm',
                                  result.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                                )}>
                                  {result.success ? '✅' : '❌'} {result.message}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {formatDate(result.timestamp.toISOString())}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                        No actions executed yet
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Organizations Overview */}
              {systemStats.organizations.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">Organizations Overview</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Recent organizations in the system</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {systemStats.organizations.map((org) => (
                        <div 
                          key={org._id} 
                          className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">
                              {org.name}
                            </h3>
                            <span className={cn(
                              'px-2 py-1 text-xs rounded-full',
                              org.isActive 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                            )}>
                              {org.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            <div>{org.userCount} users</div>
                            <div>Created {formatDate(org.createdAt)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* User Statistics */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">User Statistics</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">User distribution by role</p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(systemStats.users.byRole).map(([role, count]) => (
                      <div key={role} className="text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {count}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                          {role === 'implementor' ? 'Implementors' : `${role}s`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </PageLayout>
    </AdminPanelErrorBoundary>
  );
};